import { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogAction,
  DialogCancel,
  DialogContent,
  DialogFooter,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { useToast } from '@wartek-id/toast-v2'

import { queryKeys } from 'api/kinerja/tim-penilai-ps/config'
import { TableContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/TableContext'
import {
  PERIODIK_PERFORMANCE_PRACTICE_RESULTS,
  PERIODIK_PERFORMANCE_PREDICATE_RATING_MAPPING,
  PERIODIK_WORK_BEHAVIOR_RESULTS,
} from 'app/KinerjaShared/constants/periodik'
import { PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS } from 'app/KinerjaTimPenilaiPSPage/constants/penetapan'
import { useAPISavePenetapanPegawai } from 'api/kinerja/tim-penilai-ps/penetapan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { usePenetapanTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePenetapanTrackEvent'

import type {
  PenetapanPredikatKinerjaComponent,
  RequestSavePenetapanPegawai,
} from 'types/kinerja-tim-penilai-ps'
import type { PenetapanPegawaiPSItem } from 'types/kinerja-tim-penilai-ps'

type FormFields = Pick<
  PenetapanPredikatKinerjaComponent,
  'praktikKinerjaRating' | 'perilakuKerjaRating'
>

type FormProps = {
  data: PenetapanPegawaiPSItem
  isDisabled?: boolean
  isSubmitting?: boolean
  isSubmitSuccess?: boolean
  className?: string
  onSubmit?: (payload: RequestSavePenetapanPegawai) => void
}

type ActionButtonProps = {
  data: PenetapanPegawaiPSItem
  className?: string
  onClick: () => void
}

export function ActionButtonTK({
  data,
  className,
  onClick,
}: ActionButtonProps) {
  const { predikatKinerjaTk } = data
  const isDisabled = ['', 'NOT_RATED'].includes(data.status)

  if (
    ['SENT_TO_PPK', 'SENDING_TO_BKN', 'SENT_TO_BKN', 'ERROR_SEND'].includes(
      data?.status
    )
  ) {
    return (
      <Button color="white" size="sm" className={className} onClick={onClick}>
        Cek Rincian
      </Button>
    )
  }

  if (
    data?.status === 'NOT_SENT' &&
    Boolean(predikatKinerjaTk?.predikatKinerja) &&
    predikatKinerjaTk?.predikatKinerja !== 'UNDEFINED'
  ) {
    return (
      <Button color="white" size="sm" className={className} onClick={onClick}>
        Ubah
      </Button>
    )
  }

  return (
    <Button
      color="black"
      size="sm"
      disabled={isDisabled}
      className={className}
      onClick={onClick}
    >
      Lakukan
    </Button>
  )
}

export function ActionButtonPPK({
  isDisabled: disabled = false,
  data,
  className,
}: FormProps) {
  const { trackEvent } = usePenetapanTrackEvent()
  const { namaPengawas, status, predikatKinerjaTk, predikatKinerjaFinal } = data
  const isDisabled =
    disabled ||
    !status ||
    [
      'NOT_RATED',
      'NOT_SENT',
      'SENDING_TO_BKN',
      'SENT_TO_BKN',
      'ERROR_SEND',
    ].includes(status)

  const queryClient = useQueryClient()
  const toast = useToast()
  const { period } = usePeriodContext()

  const context = useContext(TableContext)

  // Ensure that the component is used within the correct context provider
  if (!context)
    throw new Error(
      '<TablePegawaiPenetapan.ActionChangePenetapan> must be used within a <TablePegawaiPenetapan.Provider>'
    )

  // -----------------
  // Handle form sheet
  // -----------------
  const [isFormOpen, setFormOpen] = useState(false)
  const handleCloseForm = () => {
    setFormOpen(false)
  }

  const handleOpenForm = () => {
    setFormOpen(true)
  }

  // ------------------
  // Handle form fields
  // ------------------
  const formMethods = useForm<FormFields>({ mode: 'onChange' })
  const [praktikKinerjaRating, perilakuKerjaRating] = formMethods.watch([
    'praktikKinerjaRating',
    'perilakuKerjaRating',
  ])
  const predikatKinerja =
    PERIODIK_PERFORMANCE_PREDICATE_RATING_MAPPING?.[
      `${praktikKinerjaRating}-${perilakuKerjaRating}`
    ] ?? 'UNDEFINED'
  const praktikKinerjaRatingValue =
    predikatKinerjaFinal?.praktikKinerjaRating !== 'UNDEFINED'
      ? predikatKinerjaFinal?.praktikKinerjaRating
      : predikatKinerjaTk?.praktikKinerjaRating !== 'UNDEFINED'
      ? predikatKinerjaTk?.praktikKinerjaRating
      : 'UNDEFINED'
  const perilakuKerjaRatingValue =
    predikatKinerjaFinal?.perilakuKerjaRating !== 'UNDEFINED'
      ? predikatKinerjaFinal?.perilakuKerjaRating
      : predikatKinerjaTk?.perilakuKerjaRating !== 'UNDEFINED'
      ? predikatKinerjaTk?.perilakuKerjaRating
      : 'UNDEFINED'

  // Save Penetapan
  const {
    mutate: savePenetapan,
    isLoading: isSubmitting,
    isSuccess: isSubmitted,
  } = useAPISavePenetapanPegawai({})

  const resetQueries = () => {
    // Invalidate the get detail (pegawai list) query of the current page
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_STATUS,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_PREDICATE,
      {
        period: period.id,
      },
    ])
  }

  const handleSubmitPenetapan = (payload: RequestSavePenetapanPegawai) => {
    savePenetapan(
      {
        ...payload,
        pengawasSekolahIds: [data.pengawasSekolahId],
        predikatKinerja,
      },
      {
        onSuccess: () => {
          // Show toast message
          toast({ message: 'Berhasil ubah Predikat Kinerja!', type: 'success' })

          resetQueries()

          trackEvent('tabel pegawai PPK', 'save predikat kinerja')
        },
        onError: () => {
          // Show toast message
          toast({
            message: 'Gagal simpan Predikat Kinerja. Coba lagi.',
          })
        },
      }
    )
  }

  useEffect(() => {
    if (
      isFormOpen &&
      Boolean(praktikKinerjaRatingValue) &&
      Boolean(perilakuKerjaRatingValue)
    ) {
      formMethods.reset({
        praktikKinerjaRating: praktikKinerjaRatingValue,
        perilakuKerjaRating: perilakuKerjaRatingValue,
      })
    } else {
      formMethods.reset()
    }
  }, [
    formMethods,
    isFormOpen,
    praktikKinerjaRatingValue,
    perilakuKerjaRatingValue,
  ])

  // Upon successful submit: close the form and reset selected items
  useEffect(() => {
    if (isSubmitted) {
      handleCloseForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted])

  return (
    <>
      <Button
        color={isDisabled ? 'black' : 'white'}
        size="sm"
        disabled={isDisabled}
        className={className}
        onClick={handleOpenForm}
      >
        Ubah Predikat Kinerja
      </Button>

      <Dialog isOpen={isFormOpen} onClose={handleCloseForm} size="md">
        <DialogOverlay />
        <DialogContent className="!rounded-xl">
          <div className="rounded-t-lg bg-gray-5 ">
            <Text variant="heading-md" className="p-6">
              Ubah Predikat Kinerja
            </Text>
          </div>
          <div className="p-5 space-y-6">
            <Text color="subdued">
              Predikat Kinerja akan otomatis berubah berdasarkan Rating Praktik
              Kinerja dan Rating Perilaku Kerja yang Anda pilih. Diskusikan
              dengan Tim Kinerja jika perlu.
            </Text>

            <form className="space-y-5">
              <fieldset>
                <Text
                  as="label"
                  htmlFor="rating-praktik-kinerja"
                  className="block mb-2"
                >
                  Rating Praktik Kinerja
                </Text>
                <Controller
                  control={formMethods.control}
                  rules={{ required: true }}
                  name="praktikKinerjaRating"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        id="rating-praktik-kinerja"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={isDisabled}
                      >
                        <option className="text-disabled" selected value="">
                          Pilih di sini
                        </option>
                        {Object.entries(
                          PERIODIK_PERFORMANCE_PRACTICE_RESULTS
                        ).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    )
                  }}
                />
              </fieldset>
              <fieldset>
                <Text
                  as="label"
                  htmlFor="rating-perilaku-kerja"
                  className="block mb-2"
                >
                  Rating Perilaku Kerja
                </Text>
                <Controller
                  control={formMethods.control}
                  rules={{ required: true }}
                  name="perilakuKerjaRating"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        id="rating-perilaku-kerja"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={isDisabled}
                      >
                        <option className="text-disabled" selected value="">
                          Pilih di sini
                        </option>
                        {Object.entries(PERIODIK_WORK_BEHAVIOR_RESULTS).map(
                          ([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          )
                        )}
                      </Select>
                    )
                  }}
                />
              </fieldset>
              <fieldset>
                <Text
                  as="label"
                  htmlFor="rating-perilaku-kerja"
                  className="block mb-2"
                >
                  Predikat Kinerja
                </Text>

                <div className="rounded-md border border-[#53575A] flex justify-between">
                  <div className="p-4">
                    <Text>{namaPengawas}</Text>
                  </div>
                  <div className="w-5/12 p-4 bg-black">
                    <Text
                      color="inverse"
                      variant="heading-sm"
                      className="!font-bold text-center"
                    >
                      {PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS?.[
                        predikatKinerja
                      ] ?? '-'}
                    </Text>
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
          <DialogFooter>
            <div className="flex space-x-3">
              <DialogCancel as={Button} color="white" onClick={handleCloseForm}>
                Kembali
              </DialogCancel>
              <DialogAction
                as={Button}
                onClick={formMethods.handleSubmit(handleSubmitPenetapan)}
                color="blue"
                isLoading={isSubmitting}
                disabled={
                  !formMethods.formState.isDirty ||
                  !formMethods.formState.isValid ||
                  isSubmitting
                }
                type="submit"
              >
                Simpan
              </DialogAction>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
